import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import { ClientApiProps, IAssessmentEx, fileAcceptString, yupFileTestOptions } from '../../common';
import {
  Button,
  Checkbox,
  ErrorScope,
  Label,
  NumberInput,
  Select,
  SelectOption,
  Spinner,
  TextArea,
  TextInput,
  ValidationHint,
  useValidatedState,
} from 'acma-spa';
import {
  Acma_amateurradioassessment_statuscode,
  Acma_amateurradioassessmentmethods,
  Acma_amateurradioassessmenttypes,
  Acma_amateurradioexamresults,
  Acma_amateurradioexamstatuses,
  Assessment,
  Client,
  IAssessment,
  getApiClient,
  msalGetToken,
} from '../../api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { Link } from '../../common/FormHelpers';

export const schema: yup.ObjectSchema<IAssessmentEx> = yup.object({
  id: yup.string(),
  referenceNumber: yup.string(),
  title: yup.string(),
  firstName: yup.string().required().label('First Name'),
  lastName: yup.string().required().label('Last Name'),
  otherName: yup.string(),
  dob: yup.date(),
  emailAddress: yup.string().email().required().label('Email address'),
  type: yup.mixed<Acma_amateurradioassessmenttypes>().oneOf(Object.values(Acma_amateurradioassessmenttypes)).required().label('Theoretical component level'),
  method: yup.mixed<Acma_amateurradioassessmentmethods>().oneOf(Object.values(Acma_amateurradioassessmentmethods)).required().label('Method'),
  theoreticalComponent: yup
    .mixed<Acma_amateurradioexamstatuses>()
    .oneOf(Object.values(Acma_amateurradioexamstatuses))
    .required()
    .label('Theoretical examination'),
  theoreticalScore: yup
    .number()
    .label('Theoretical component score')
    .when(['theoreticalComponent'], {
      is: (theoreticalComponent?: Acma_amateurradioexamstatuses) => theoreticalComponent == Acma_amateurradioexamstatuses.ExaminationTakenUnderMySupervision,
      then: (schema) => schema.min(0).max(100).required(),
    }),
  regulatoryComponent: yup
    .mixed<Acma_amateurradioexamstatuses>()
    .oneOf(Object.values(Acma_amateurradioexamstatuses))
    .required()
    .label('Regulatory examination'),
  regulatoryScore: yup
    .number()
    .label('Regulatory component score')
    .when(['regulatoryComponent'], {
      is: (regulatoryComponent?: Acma_amateurradioexamstatuses) => regulatoryComponent == Acma_amateurradioexamstatuses.ExaminationTakenUnderMySupervision,
      then: (schema) => schema.min(0).max(100).required(),
    }),
  practicalComponent: yup.mixed<Acma_amateurradioexamstatuses>().oneOf(Object.values(Acma_amateurradioexamstatuses)).required().label('Practical examination'),
  practicalResult: yup
    .mixed<Acma_amateurradioexamresults>()
    .oneOf(Object.values(Acma_amateurradioexamresults))
    .label('Practical component result')
    .when(['practicalComponent'], {
      is: (practicalComponent?: Acma_amateurradioexamstatuses) => practicalComponent == Acma_amateurradioexamstatuses.ExaminationTakenUnderMySupervision,
      then: (schema) => schema.required(),
    }),
  createdOn: yup.date(),
  fileNames: yup.array().defined('At least one document is required').min(1, 'At least one document is required').test(yupFileTestOptions),
  existingQualification: yup.boolean().required('Answer is required'),
  existingQualificationComments: yup.string().label('Comments') /*.when(['existingQualification'], {
    is: (existingQualification?: boolean) => existingQualification == true,
    then: (schema) => schema.required('Answer is required'),
  })*/,
  specialExamination: yup.boolean().required('Answer is required'),
  specialExaminationComments: yup.string().label('Comments') /*.when(['specialExamination'], {
    is: (specialExamination?: boolean) => specialExamination == true,
    then: (schema) => schema.required('Answer is required'),
  })*/,
  confirmation: yup.boolean().required('Confirmation required').isTrue('Confirmation required'),
  status: yup.mixed<Acma_amateurradioassessment_statuscode>().oneOf(Object.values(Acma_amateurradioassessment_statuscode)).optional(),
});

export interface AssessmentDetailsProps extends ClientApiProps {
  row: IAssessment | null;
  onChange: (row: IAssessment | null) => void;
}

const getTypes = () => {
  const options: SelectOption<Acma_amateurradioassessmenttypes>[] = [];
  for (const enumMember in Acma_amateurradioassessmenttypes) {
    options.push({ label: enumMember, value: enumMember as Acma_amateurradioassessmenttypes });
  }
  return options;
};

const getMethods = () => {
  const options: SelectOption<Acma_amateurradioassessmentmethods>[] = [];
  for (const enumMember in Acma_amateurradioassessmentmethods) {
    if (enumMember == Acma_amateurradioassessmentmethods.PreviousQualification || enumMember == Acma_amateurradioassessmentmethods.PriorLearningRecognition || enumMember == Acma_amateurradioassessmentmethods.DataMigration) {
      continue;
    }
    options.push({
      label:
        enumMember
          ?.match(/[A-Z][a-z]+|[0-9]+/g)
          ?.map((r, i) => (i > 0 ? r.toLowerCase() : r))
          .join(' ') ?? '',
      value: enumMember as Acma_amateurradioassessmentmethods,
    });
  }
  return options;
};

const getExamStatuses = () => {
  return [
    { label: 'Examination component taken under my supervision', value: Acma_amateurradioexamstatuses.ExaminationTakenUnderMySupervision },
    { label: 'Examination component completed in the past', value: Acma_amateurradioexamstatuses.ExaminationPassedInThePast },
    { label: 'Examination component scheduled for a future date', value: Acma_amateurradioexamstatuses.ExaminationIsScheduledForFutureDate },
    { label: "Passed component after a 'recognition of prior learning' assessment", value: Acma_amateurradioexamstatuses.PassedUnderPriorLearningRecognition },
  ];
};

const getExamResults = () => {
  const options: SelectOption<Acma_amateurradioexamresults>[] = [];
  for (const enumMember in Acma_amateurradioexamresults) {
    options.push({
      label:
        enumMember
          ?.match(/[A-Z][a-z]+|[0-9]+/g)
          ?.map((r, i) => (i > 0 ? r.toLowerCase() : r))
          .join(' ') ?? '',
      value: enumMember as Acma_amateurradioexamresults,
    });
  }
  return options;
};

export const AssessmentDetails: React.FC<AssessmentDetailsProps> = (props) => {
  const [model, setModel, errors, validate] = useValidatedState<IAssessmentEx>(
    props.row ? { ...props.row, confirmation: true } : { fileNames: [], status: Acma_amateurradioassessment_statuscode.Reported },
    schema,
  );
  const [files, setFiles] = useState<Record<string, File | null>>({});
  const [spinner, setSpinner] = useState('');
  const readonly = props.row != null && props.row.status != Acma_amateurradioassessment_statuscode.Reported;

  // Helper to quickly determine if we have evidence-attachments.
  const hasFiles: boolean = (model.fileNames?.length || 0) > 0;

  const load = async (client: Client) => {
    setSpinner('Loading...');
    try {
      const res = await client.assessmentsGetDetails(model.id);
      setModel({ fileNames: res }, ValidationHint.NoValidation);
      const newFiles: Record<string, File | null> = {};
      for (const f of res) {
        newFiles[f] = null;
      }
      setFiles(newFiles);
    } catch (e) {
      setModel({ fileNames: [] }, ValidationHint.NoValidation);
    } finally {
      setSpinner('');
    }
  };

  useEffect(() => {
    if (!model.id) return;

    const [client, abort] = getApiClient(props.baseUrl, props.b2cDomainName, props.msalClient);

    load(client);

    return function () {
      abort.abort();
    };
  }, [model.id]);

  const onFileAdded = (arr: File[]) => {
    const newFiles = { ...files };
    for (const f of arr) {
      const index = f.name.lastIndexOf('.');
      let filename = f.name;
      let extension = '';

      if (index >= 0) {
        filename = f.name.substring(0, index);
        extension = f.name.substring(index);
      }

      for (let j = 0; ; j++) {
        let key = f.name;
        if (j > 0) {
          key = `${filename} (${j})${extension}`;
        }
        if (!(key in newFiles)) {
          newFiles[key] = f;
          break;
        }
      }
    }
    setFiles(newFiles);
    return Object.keys(newFiles);
  };

  const onFileRemoved = (name: string) => {
    const newFiles = { ...files };
    if (name in newFiles) delete newFiles[name];
    setFiles(newFiles);
    return Object.keys(newFiles);
  };

  const containerId = 'assessment-details';

  const onChange = async () => {
    const isValid = await validate('#' + containerId);
    if (!isValid) {
      return;
    }

    setSpinner('Saving...');
    try {
      const formData = new FormData();
      formData.append('data', JSON.stringify(new Assessment(model).toJSON()));

      const fileNames: string[] = [];
      for (const name in files) {
        const f = files[name];
        if (f == null) continue;

        formData.append('files', f);
        fileNames.push(name);
      }

      formData.append('fileNames', JSON.stringify(fileNames));

      const authToken = await msalGetToken(props.b2cDomainName, props.msalClient);
      const res = await fetch(`${props.baseUrl}/api/Assessments`, {
        method: 'POST',
        body: formData,
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (res.status == 200 || res.status == 204) {
        const id = await res.json();
        props.onChange({ ...model, id: id, createdOn: model.createdOn ?? new Date() });
      }
    } finally {
      setSpinner('');
    }
  };

  const yesNoOptions = [
    { label: 'Yes', value: 1 },
    { label: 'No', value: -1 },
  ];

  const scoreOptions = { allowNegative: false, scale: 2, normalizeZeros: true, radix: '.' } as Omit<IMask.MaskedNumber, 'mask'>;

  return (
    <div id={containerId}>
      <Spinner isLoading={spinner ? true : false} loadingText={spinner}>
        <p>
          <b>Assessor instructions</b>
        </p>
        <ul>
          <li>Use a separate form for each candidate to submit results</li>
          <li>One or more components may be submitted – additional examination results can be entered at other times</li>
        </ul>
        <div className="row">
          <div className="col-sm-4 mb-3">
            <Label id="firstName" label="First name" error={errors?.firstName} required={true}>
              <TextInput value={model.firstName || ''} onChange={(v) => setModel({ firstName: v })} maxLength={149} readonly={readonly} />
            </Label>
          </div>
          <div className="col-sm-4 mb-3">
            <Label id="lastName" label="Last name" error={errors?.lastName} required={true}>
              <TextInput value={model.lastName || ''} onChange={(v) => setModel({ lastName: v })} maxLength={149} readonly={readonly} />
            </Label>
          </div>
          <div className="col-sm-4 mb-3">
            <Label id="otherName" label="Other name" error={errors?.otherName}>
              <TextInput value={model.otherName || ''} onChange={(v) => setModel({ otherName: v })} maxLength={149} readonly={readonly} />
            </Label>
          </div>
          <div className="col-sm-4 mb-3">
            <Label id="emailAddress" label="Email" error={errors?.emailAddress} required={true}>
              <TextInput value={model.emailAddress || ''} onChange={(v) => setModel({ emailAddress: v })} maxLength={400} readonly={readonly} />
            </Label>
          </div>
          <div className="col-sm-4 mb-3">
            <Label id="type" label="Theoretical component level" error={errors?.type} required={true}>
              <Select<Acma_amateurradioassessmenttypes>
                options={getTypes()}
                value={model.type || null}
                onChange={(type) => setModel({ type: type || undefined })}
                defaultLabel="Select..."
                size="block"
                disabled={readonly}
              />
            </Label>
          </div>
          <div className="col-sm-4 mb-3">
            <Label id="method" label="Method" error={errors?.method} required={true}>
              <Select<Acma_amateurradioassessmentmethods>
                options={getMethods()}
                value={model.method || null}
                onChange={(method) => setModel({ method: method || undefined })}
                defaultLabel="Select..."
                size="block"
                disabled={readonly}
              />
            </Label>
          </div>
        </div>

        <div className="row">
          <div className="col mb-3">
            <ErrorScope id="confirmation" error={errors?.confirmation}>
              <Checkbox
                id="confirmation"
                label="I have sighted this candidate’s photo identification and confirm it matches the details here."
                checked={readonly ? true : model.confirmation ?? false}
                onChange={(v) => setModel({ confirmation: v })}
                disabled={readonly}
                required
              />
            </ErrorScope>
          </div>
        </div>

        <div className="row">
          <div className="col-sm-4 mb-3">
            <Label id="theoreticalComponent" label="Theoretical examination" error={errors?.theoreticalComponent} required={true}>
              <Select<Acma_amateurradioexamstatuses>
                options={getExamStatuses()}
                value={model.theoreticalComponent || null}
                onChange={(v) => setModel({ theoreticalComponent: v || undefined })}
                defaultLabel="Select..."
                size="block"
                disabled={readonly}
              />
            </Label>
          </div>
          {model.theoreticalComponent == Acma_amateurradioexamstatuses.ExaminationTakenUnderMySupervision ? (
            <>
              <div className="col-sm-4 mb-3">
                <Label id="theoreticalScore" label="Theoretical component score" error={errors?.theoreticalScore} required={true}>
                  <NumberInput
                    value={model.theoreticalScore || null}
                    onChange={(v) => setModel({ theoreticalScore: v == null ? undefined : v })}
                    readonly={readonly}
                    options={scoreOptions}
                  />
                </Label>
              </div>
              <div className="col-sm-4 mb-3">
                <Label id="" label="&nbsp;" className="d-none d-sm-block" />
                <div className="mt-sm-3">
                  {model.theoreticalScore != undefined && (
                    <b style={{ color: model.theoreticalScore >= 70 ? 'green' : 'red' }}>{model.theoreticalScore >= 70 ? 'Pass' : 'Fail'}</b>
                  )}
                </div>
              </div>
            </>
          ) : (
            <></>
          )}
        </div>

        <div className="row">
          <div className="col-sm-4 mb-3">
            <Label id="regulatoryComponent" label="Regulatory examination" error={errors?.regulatoryComponent} required={true}>
              <Select<Acma_amateurradioexamstatuses>
                options={getExamStatuses()}
                value={model.regulatoryComponent || null}
                onChange={(v) => setModel({ regulatoryComponent: v || undefined })}
                defaultLabel="Select..."
                size="block"
                disabled={readonly}
              />
            </Label>
          </div>
          {model.regulatoryComponent == Acma_amateurradioexamstatuses.ExaminationTakenUnderMySupervision ? (
            <>
              <div className="col-sm-4 mb-3">
                <Label id="regulatoryScore" label="Regulatory component score" error={errors?.regulatoryScore} required={true}>
                  <NumberInput
                    value={model.regulatoryScore || null}
                    onChange={(v) => setModel({ regulatoryScore: v == null ? undefined : v })}
                    readonly={readonly}
                    options={scoreOptions}
                  />
                </Label>
              </div>
              <div className="col-sm-4 mb-3">
                <Label id="" label="&nbsp;" className="d-none d-sm-block" />
                <div className="mt-sm-3">
                  {model.regulatoryScore != undefined && (
                    <b style={{ color: model.regulatoryScore >= 70 ? 'green' : 'red' }}>{model.regulatoryScore >= 70 ? 'Pass' : 'Fail'}</b>
                  )}
                </div>
              </div>
            </>
          ) : (
            <></>
          )}
        </div>

        <div className="row">
          <div className="col-sm-4 mb-3">
            <Label id="practicalComponent" label="Practical examination" error={errors?.practicalComponent} required={true}>
              <Select<Acma_amateurradioexamstatuses>
                options={getExamStatuses()}
                value={model.practicalComponent || null}
                onChange={(v) => setModel({ practicalComponent: v || undefined })}
                defaultLabel="Select..."
                size="block"
                disabled={readonly}
              />
            </Label>
          </div>
          {model.practicalComponent == Acma_amateurradioexamstatuses.ExaminationTakenUnderMySupervision ? (
            <>
              <div className="col-sm-4 mb-3">
                <Label id="practicalResult" label="Practical component result" error={errors?.practicalResult} required={true}>
                  <Select<Acma_amateurradioexamresults>
                    options={getExamResults()}
                    value={model.practicalResult || null}
                    onChange={(v) => setModel({ practicalResult: v || undefined })}
                    defaultLabel="Select..."
                    size="block"
                    disabled={readonly}
                  />
                </Label>
              </div>
            </>
          ) : (
            <></>
          )}
        </div>

        <div className="row">
          <div className="col mb-3">
            <Label
              id="existingQualification"
              error={errors?.existingQualification}
              required={true}
              label="Does the candidate hold a domestic amateur radio qualification?">
              <Select<number>
                options={yesNoOptions}
                defaultLabel="Select..."
                disabled={readonly}
                value={model.existingQualification === true ? 1 : model.existingQualification === false ? -1 : null}
                onChange={(v) => setModel({ existingQualification: v === 1 ? true : v == -1 ? false : undefined })}
              />
            </Label>
          </div>
        </div>

        <div className="row" style={{ display: model.existingQualification ? undefined : 'none' }}>
          <div className="col mb-3">
            <Label
              id="existingQualificationComments"
              error={errors?.existingQualificationComments}
              label={<span>Please consider which components of the examination the candidate needs to undertake with reference to the tables contained in the {Link(
                props.contentUrls.callSignPolicy,
                'ACMA Amateur Radio Qualification Framework',
              )}. Please indicate in this textbox which components you told the candidate they need to undertake and why.</span>}>
              <TextArea
                readonly={readonly}
                maxLength={2000}
                value={model.existingQualificationComments ?? ''}
                onChange={(existingQualificationComments) => setModel({ existingQualificationComments })}
              />
            </Label>
          </div>
        </div>

        <div className="row">
          <div className="col mb-3">
            <Label id="specialExamination" error={errors?.specialExamination} required={true} label="Was this a special examination?">
              <Select<number>
                options={yesNoOptions}
                defaultLabel="Select..."
                disabled={readonly}
                value={model.specialExamination === true ? 1 : model.specialExamination === false ? -1 : null}
                onChange={(v) => setModel({ specialExamination: v === 1 ? true : v == -1 ? false : undefined })}
              />
            </Label>
          </div>
        </div>

        <div className="row" style={{ display: model.specialExamination ? undefined : 'none' }}>
          <div className="col mb-3">
            <Label
              id="specialExaminationComments"
              error={errors?.specialExaminationComments}
              label="Please outline here what method was used to undertake the examination and provide your view on the candidates competency.  Please also provide a short report on how the method used in the examination did not compromise the integrity of the examination.">
              <TextArea
                readonly={readonly}
                maxLength={2000}
                value={model.specialExaminationComments ?? ''}
                onChange={(specialExaminationComments) => setModel({ specialExaminationComments })}
              />
            </Label>
          </div>
        </div>

        <div className="row">
          <div className="col-sm-12 mb-3">
            <Label id="files" label="Evidence" error={errors.fileNames} required>
              <p>Please upload the following documents:</p>
              <ul>
                <li>
                  If you conducted a theoretical and/or regulations examinations – a copy of the candidate's completed cover sheets (2 pages) and answer sheets (2 pages including the before you leave sheet)
                </li>
                <li>
                  If you conducted a practical examination – a copy of the cover sheets (2 pages) and practical assessment sheets (4 pages)
                </li>
                <li>
                  A copy of any correspondence you exchanged with the candidate about the time, place or conditions of the examination
                </li>
                <li>
                  If the examination was a remote examination, conducted by telephone – a copy of email approval you received from the ACMA to complete the examination under these conditions, and a copy of a signed document from the invigilator confirming that the examination conditions were met
                </li>
              </ul>
              {!hasFiles ? (
                <p className="mt-1">No evidence files uploaded.</p>
              ) : (
                <ul className="list-unstyled mt-2">
                  {model.fileNames?.map((r, i) => (
                    <li key={i}>
                      <span style={{ fontFamily: 'monospace' }}>{r}</span>
                      <a
                        href="#"
                        title="Remove"
                        style={{ display: readonly ? 'none' : '', marginLeft: '0.5rem' }}
                        onClick={(e) => {
                          e.preventDefault();
                          const fileNames = onFileRemoved(r);
                          setModel({ fileNames });
                        }}>
                        <FontAwesomeIcon icon={faXmark} />
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </Label>
            {readonly ? <></> : <Button id="fileNames" label="Add file" onClick={() => document.getElementById('document-upload')?.click()} />}

            <input
              type="file"
              id="document-upload"
              style={{ display: 'none' }}
              multiple={true}
              accept={fileAcceptString}
              onChange={(e) => {
                const fileNames = onFileAdded(e.target.files ? Array.from(e.target.files) : []);
                setModel({ fileNames });
                e.target.value = '';
              }}
            />
          </div>
        </div>

        <hr />

        {readonly ? <></> : <Button label={props.row ? 'Resubmit' : 'Submit'} onClick={() => onChange()} />}
        <Button label="Cancel" mode="secondary" onClick={() => props.onChange(props.row)} className="ml-2" />
      </Spinner>
    </div>
  );
};
